import axios, { AxiosInstance, AxiosError } from 'axios';
import toast from 'react-hot-toast';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8001',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('dns-fabric-token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    const message = getErrorMessage(error);

    // Handle specific error cases
    if (error.response?.status === 401) {
      // Allow GitHub integration endpoints to surface 401 without logging the user out
      const url = error.config?.url || '';
      const isGithubIntegration = url.includes('/api/integrations/github');
      if (!isGithubIntegration) {
        localStorage.removeItem('dns-fabric-token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        toast.error('Session expired. Please log in again.');
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 403) {
      toast.error('You do not have permission to perform this action.');
      return Promise.reject(error);
    }

    if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
      return Promise.reject(error);
    }

    // For other errors, show the specific message
    if (message && error.response?.status !== 404) {
      toast.error(message);
    }

    return Promise.reject(error);
  },
);

function getErrorMessage(error: AxiosError): string {
  if (error.response?.data) {
    const data = error.response.data as any;

    // Try different possible error message fields
    if (data.error) return data.error;
    if (data.message) return data.message;
    if (data.detail) return data.detail;
    if (data.msg) return data.msg;

    // If it's a string, return it directly
    if (typeof data === 'string') return data;
  }

  // Fallback messages based on status code
  switch (error.response?.status) {
    case 400:
      return 'Bad request. Please check your input.';
    case 401:
      return 'Authentication required.';
    case 403:
      return 'Access forbidden.';
    case 404:
      return 'Resource not found.';
    case 422:
      return 'Validation error. Please check your input.';
    case 429:
      return 'Too many requests. Please try again later.';
    case 500:
      return 'Internal server error.';
    case 502:
      return 'Bad gateway.';
    case 503:
      return 'Service unavailable.';
    case 504:
      return 'Gateway timeout.';
    default:
      return error.message || 'An unexpected error occurred.';
  }
}
